import { Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Carousel } from 'primeng/carousel';
import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICartItem, ICombinedExtra, IExternalExtra, IExtra, IExtraCartItem, IShopCart, SettingsService, priceExtraForRoom } from 'core-lib';
import { ShopcartService } from 'core-lib';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { IncrementorComponent } from "core-lib";
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { EditExtraDialogComponent } from "../edit-extra-dialog/edit-extra-dialog.component";
import { ExtraDebugviewComponent } from "../extra-debugview/extra-debugview.component";
import { trigger, transition, style, animate} from '@angular/animations';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TruncatedTextComponent } from "../truncated-text/truncated-text.component";
import { getExtraUiType, getAutoQty, getExtraMaxQty, computeUnitPrice, ExtraUiType } from './extras-pricing.helper';



@Component({
    selector: 'app-extras',
    providers: [CurrencyPipe],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('1000ms ease-out', style({ opacity: 1 }))
            ])
        ])
    ],
    imports: [Carousel, ButtonModule, CurrencyPipe, TranslateModule, DialogModule, SelectModule, FormsModule, IncrementorComponent, ProgressSpinnerModule, EditExtraDialogComponent, ExtraDebugviewComponent, DatePipe, NgTemplateOutlet, SelectButtonModule, TruncatedTextComponent],
    templateUrl: './extras.component.html',
    styleUrl: './extras.component.scss'
})
export class ExtrasComponent implements OnInit{

  @Input() extras: IExtra[];
  @Input() externalExtras: IExternalExtra[];
  @Input() ticketExtraId:undefined|string;
  @ViewChild('carouselTop') carouselTop!: ElementRef;
  combinedExtras:ICombinedExtra[] = [];
  responsiveOptions: any[] | undefined;
  language: string;
  extraEditDialogOpen: boolean = false;
  roomOptions: any[] = [];
  selectedRoom:any;
  extrasToEdit:null|IExtraCartItem[] = null
  shopCart:IShopCart;
  showLoaderId:null|string=null;
  roomPersons:number=10;
  carouselMode:boolean = true;

  viewOptions: any[]



  constructor(private shopCartService: ShopcartService, private translateService: TranslateService, public settings: SettingsService, private currencyPipe: CurrencyPipe) {

  }

  ngOnInit() {
    this.viewOptions = [
      { label: this.translateService.instant('pr.viewCarousel'), value: true },
      { label: this.translateService.instant('pr.viewList'), value: false }
    ]
    if (this.settings.get('profile.showExtrasViewOptions')) {
      this.carouselMode = false;
    }
    this.language = this.translateService.currentLang;
    
    this.responsiveOptions = [
        {
            breakpoint: '991px',
            numVisible: 2,
            numScroll: 1
        },
        {
            breakpoint: '767px',
            numVisible: 1,
            numScroll: 1
        }
    ];

    this.shopCartService.shopCart$.subscribe((res:IShopCart|null)  => {
      if (res) {
        this.shopCart = res;
        this.roomPersons = this.shopCart.getFirstSearchParams().roomData[0].adults + this.shopCart.getFirstSearchParams().roomData[0].children
      }
    });

    this.shopCartService.getCart()?.items.forEach((room:ICartItem, index) => {
      let roomName = room.item.room.pim?.name  ?? room.item.room.roomName;
      this.roomOptions.push({
        value: index,
        label: `${index+1}: ${roomName}`
      });
    });

    this.combinedExtras = this.combineExtrasAndExternalExtras();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['extras'] || changes['externalExtras']) {
      this.combinedExtras = this.combineExtrasAndExternalExtras();
    }
  }

  combineExtrasAndExternalExtras() {
    let mapped:ICombinedExtra[] = [];
    this.externalExtras?.forEach((externalExtra:IExternalExtra) => {
      mapped.push(this.mapExternalExtraToExtra(externalExtra));
    });

    return this.extras.concat(mapped);
  }

  mapExternalExtraToExtra(externalExtra:IExternalExtra) {
    let rooms = this.shopCartService.getCart()?.items || [];
    let allRoomIndexes = rooms.map((_, index) => index);

    let mapped:ICombinedExtra = {
      event: externalExtra.event,
      externalId: externalExtra.externalId,
      rateId: this.ticketExtraId+"|"+externalExtra.externalId,
      extraName: `${externalExtra.event.name} - ${externalExtra.name}`,
      calculationRule: "",
      postingRhythm: "",
      totalPriceForStayWithTaxes: externalExtra.price,
      unitPriceForStayWithTaxes: externalExtra.price,
      signature: externalExtra.signature,
      description: externalExtra.description,
      availableRoomIndexes: allRoomIndexes,
      remainingQuantity: externalExtra.remainingQuantity,
      imageUrl: externalExtra.imageData ?? undefined,
    };
    return mapped;
  }

  getExtraUiType(extra: IExtra): ExtraUiType {
    if ((extra as any).externalId) return 'incrementor';
    return getExtraUiType(extra);
  }

  getExtraAutoQty(extra: IExtra): number {
    const roomData = this.shopCart?.getFirstSearchParams()?.roomData?.[0];
    return getAutoQty(extra, roomData?.adults ?? 0, roomData?.children ?? 0);
  }

  getUnitPrice(extra: IExtra): number {
    const nights = this.shopCart?.getDaysDelta() ?? 1;
    const roomData = this.shopCart?.getFirstSearchParams()?.roomData?.[0];
    const adults = roomData?.adults ?? 0;
    const children = roomData?.children ?? 0;
    return computeUnitPrice(extra, nights, adults, children);
  }

  getExtraMaxQty(extra: IExtra): number {
    const nights = this.shopCart?.getDaysDelta() ?? 1;
    const roomData = this.shopCart?.getFirstSearchParams()?.roomData?.[0];
    const persons = (roomData?.adults ?? 0) + (roomData?.children ?? 0);
    return getExtraMaxQty(extra, nights, persons);
  }

  getPriceBreakdownKey(extra: IExtra): string {
    if ((extra as any).externalId) return '';
    const combo = `${extra.calculationRule}:${extra.postingRhythm}`;
    const keyMap: Record<string, string> = {
      'reservation:stay': 'pr.extraPricePerStay',
      'reservation:day':  'pr.extraPricePerNight',
      'room:stay':        'pr.extraPricePerRoom',
      'room:day':         'pr.extraPricePerRoomNight',
      'adult:stay':       'pr.extraPriceAdultStay',
      'adult:day':        'pr.extraPriceAdultNight',
      'child:stay':       'pr.extraPriceChildStay',
      'child:day':        'pr.extraPriceChildNight',
      'person:stay':      'pr.extraPricePersonStay',
      'person:day':       'pr.extraPricePersonNight',
    };
    return keyMap[combo] ?? '';
  }

  getPriceBreakdownParams(extra: IExtra): Record<string, string | number> {
    const nights = this.shopCart?.getDaysDelta() ?? 1;
    const roomData = this.shopCart?.getFirstSearchParams()?.roomData?.[0];
    const adults = roomData?.adults ?? 0;
    const children = roomData?.children ?? 0;
    const persons = adults + children;
    const total = extra.totalPriceForStayWithTaxes;
    const fmt = (v: number) => this.currencyPipe.transform(v) ?? String(v);
    const unit = computeUnitPrice(extra, nights, adults, children);

    switch (`${extra.calculationRule}:${extra.postingRhythm}`) {
      case 'reservation:day':
      case 'room:day':
        return { nights, unit: fmt(unit) };
      case 'adult:stay':
        return { count: adults, unit: fmt(unit), total: fmt(total) };
      case 'adult:day':
        return { count: adults, nights, unit: fmt(unit), total: fmt(total) };
      case 'child:stay':
        return { count: children, unit: fmt(unit), total: fmt(total) };
      case 'child:day':
        return { count: children, nights, unit: fmt(unit), total: fmt(total) };
      case 'person:stay':
        return { count: persons, unit: fmt(unit), total: fmt(total) };
      case 'person:day':
        return { count: persons, nights, unit: fmt(unit), total: fmt(total) };
      default:
        return {};
    }
  }

  addExtraSingleRoom(extra:IExtra, qty:number) {
    let extraCartItem = {...extra, quantity: qty};
    this.shopCartService.addExtra(extraCartItem, 0);
    
    this.setSpinner(extra);
  }
  
  editExtra(extra:IExtra) {
    let editables:IExtraCartItem[] = [];
    this.shopCart.getCartItemsByType('roomrate').forEach((item:ICartItem, index:number) => {
      let existingExtra:IExtraCartItem|undefined = item.getExtraById(extra.rateId);
      if (existingExtra) {
        editables.push(JSON.parse(JSON.stringify(existingExtra)));
      }
      else {
        // shown with the room's own price, like the cart shows it once added
        let e:any = priceExtraForRoom(JSON.parse(JSON.stringify(extra)), index);
        e.quantity = 0;
        let newExtra:IExtraCartItem = e;
        
        editables.push(newExtra)
      }
    });
    this.extrasToEdit = editables;
    //this.showEditExtraDialog();
  }

  updateEditExtraQuantity(value:number, index:number) {
    if (this.extrasToEdit) {
      this.extrasToEdit[index].quantity = value;
      this.shopCartService.editExtrasForAllRooms(this.extrasToEdit);
      this.setSpinner(this.extrasToEdit[index]);
    }
  }

/*   showEditExtraDialog() {
    this.extraEditDialogOpen = true;
  }*/
  hideEditExtraDialog() {
    this.extrasToEdit = null;
  } 

  setSpinner(extra:IExtra|IExtraCartItem) {
    this.showLoaderId = extra.rateId;
    setTimeout(() => {this.showLoaderId = null;},600);
  }
}
